<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import Spinner from "@/components/ui/Spinner.vue"
import AddMultiviewModal from "@/components/modals/AddMultiviewModal.vue"
import { useRouter } from 'vue-router'
import IconBase from '../../components/icon/IconBase.vue'
import IconBin from '../../components/icon/IconBin.vue'
import IconPlus from '../../components/icon/IconPlus.vue'
import IconChevronUpDown from '../../components/icon/IconChevronUpDown.vue'
import IconCheckmark from '../../components/icon/IconCheckmark.vue'
import IconPlay from '../../components/icon/IconPlay.vue';
import IconBookOpen from '../../components/icon/IconBookOpen.vue';
import FormButton from '../../components/Atoms/FormButton.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue'
import ViewModeSwitcher from '../../components/ui/ViewModeSwitcher.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { useNotification } from "@kyvg/vue3-notification"
import MultiviewService from '@/services/MultiviewService'
import MultiviewTeamPermissionError from "@/components/Multiview/MultiviewTeamPermissionError.vue"
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import EmptyState from '@/components/ui/EmptyState.vue'
import IconElipsisVertical from '../../components/icon/IconElipsisVertical.vue'
import IconMultiview from '../../components/icon/IconMultiview.vue'
import { sortStreams } from '@/utils'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'

const store = useStore()
const router = useRouter()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const { notify } = useNotification()

const showMultiviewModal = ref()

const { sortBy, setSort } = usePersistedSort('castr.sort.multiview', DATE_NAME_SORT, 'Newest')
const searchWidgetInput = ref('')
const sortDropdown = ref()
const selectedMultiviewId = ref(null)

const modalDEMOVideo = ref(null);

const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const maxMultiviews = computed(() => store.getters['User/maxMultiviews'])
const teamPermissionError = computed(() => store.getters['Multiview/teamPermissionError'])
const userEmail = computed(() => store.getters['User/userEmail'])

const multiviews = computed(() => store.getters['Multiview/multiviews'])
const showListControls = computed(() => (multiviews.value?.length ?? 0) > 2)

const multiviewFiltered = computed(() => {
  const filtered = multiviews.value.filter(mv =>
    mv.name.toLowerCase().includes(searchWidgetInput.value.toLowerCase())
  )
  // Multiview docs may use createdAt instead of creationTime — normalize for sortStreams.
  const withCreationTime = filtered.map((mv) => ({
    ...mv,
    creationTime: mv.creationTime || mv.createdAt || mv._id,
  }))
  return sortStreams(withCreationTime, sortBy.value) || withCreationTime
})

const sortByLabel = computed(() => sortBy.value)

const multiviewLimit = computed(() => {
  const c = maxMultiviews.value
  return typeof c === 'number' && c > 0 ? c : null
})

const multiviewHeaderUsage = computed(() => {
  if (!multiviews.value?.length) return null
  const meta = {
    used: multiviews.value.length,
    label: 'multiviews',
    labelSingular: 'multiview',
  }
  if (multiviewLimit.value) meta.limit = multiviewLimit.value
  return meta
})

const atMultiviewLimit = computed(() => {
  const max = multiviewLimit.value
  if (!max) return false
  return (multiviews.value?.length ?? 0) >= max
})

const isMultiviewCreateDisabled = computed(() =>
  (!hasLiveSubscription.value || isLiveSubscriptionExpired.value || maxMultiviews.value === 0 || atMultiviewLimit.value)
)

const multiviewCreateDisabledTitle = computed(() => {
  if (atMultiviewLimit.value) return planLimitReachedMessage('Multiview')
  return ''
})

onMounted(async () => {
  if (!store.getters['Multiview/isLoaded']) {
    store.dispatch('Ui/setLoadingStatus', true)
  } else {
    store.dispatch('Ui/setLoadingStatus', false)
  }
  try {
    await store.dispatch('Multiview/getMultiviews')
    store.dispatch('Multiview/setTeamPermissionError', { error: false })
  } catch (err) {
    if (err && err.toString().includes('not have access to')) {
      store.dispatch('Multiview/setTeamPermissionError', { error: true })
    }

    notify({
      group: "error",
      text: err || "Multiview load failed",
    });
  }
  store.dispatch('Ui/setLoadingStatus', false)
})

const onNewMV = async (player) => {
  try {
    await store.dispatch('Multiview/getMultiviews', true)
    await router.push({
      name: "MultiviewContent",
      params: {
        id: player._id
      },
      query: { 'sourceTab': 'livestream' }
    })
  } catch (err) {
    store.dispatch('Ui/setLoadingStatus', false)
    notify({ group: 'error', text: err?.message || 'Could not open the new multiview' })
  }
}

const onDeleteMultiviewRequest = (id) => {
  showModal('delete-multiview')
  selectedMultiviewId.value = id
}

const multiviewStreamsNum = (mv) => {
  return _.filter(mv.streams, (stream) => stream !== null).length;
}

const onMultiviewDelete = async () => {
  if (!selectedMultiviewId.value) {
    return
  }

  console.log('selectedMultiviewId.value', selectedMultiviewId.value)

  try {
    await MultiviewService.deleteMultiview(selectedMultiviewId.value)

    store.dispatch('Multiview/removeMultiview', { id: selectedMultiviewId.value })
    selectedMultiviewId.value = null

    notify({
      group: "success",
      text: "Multiview deleted",
    });
  } catch (err) {
    notify({
      group: "error",
      text: err.message || "Unable to delete the multiview.",
    });
  }
}

const changeSortBy = (option) => {
  setSort(option)
  sortDropdown.value?.toggleDropdown?.()
}
const showCreateMultiviewModal = () => {
  showMultiviewModal.value.toggleSidebar()
}
const showDemo = () => {
  modalDEMOVideo.value.show()
}

</script>

<template>
  <div class="container detail-shell px-4">
    <PageHeader
      title="Multiview"
      subtitle="Monitor multiple live sources together in a single view."
      mobile-inline
      :usage="!loadingStatus ? multiviewHeaderUsage : null"
    >
      <Tooltip
        v-if="!loadingStatus && !((!hasLiveSubscription || isLiveSubscriptionExpired || maxMultiviews === 0) && multiviews.length === 0)"
        :active="atMultiviewLimit"
        wrap-trigger
        :html="true"
        :hide="1000"
      >
        <FormButton
          class="gap-1 shrink-0"
          size="md"
          :disabled="isMultiviewCreateDisabled"
          @click="showCreateMultiviewModal"
        >
          <icon-base class="mr-1.5" :width="14" :height="14">
            <icon-plus />
          </icon-base>
          New <span class="hidden md:block">Multiview</span>
        </FormButton>
        <template #content>
          <span class="text-surface-8 text-sm" v-html="multiviewCreateDisabledTitle" />
        </template>
      </Tooltip>
    </PageHeader>
    <Spinner v-if="loadingStatus" text="Loading your multiviews…" classes="text-surface-8 mt-96"
      spinner-color="var(--c-dark-8)" spinner-size="15px" />

    <main
      v-else-if="(!hasLiveSubscription || isLiveSubscriptionExpired || maxMultiviews === 0) && multiviews.length === 0">
      <EmptyState title="Upgrade to unlock Multiview"
        subtitle="Monitor multiple live sources side by side in one view. It's not included in your current plan.">
        <template #icon>
          <icon-base :width="28" :height="28" fill="none">
            <IconMultiview />
          </icon-base>
        </template>
        <template #actions>
          <FormButton type="secondary" class="w-64 justify-center"
            href="https://docs.castr.com/en/articles/10363776-multiview-monitoring-multiple-livestreams-at-once">
            <icon-base class="mr-2">
              <icon-book-open />
            </icon-base>
            How Multiview works
          </FormButton>
          <FormButton type="secondary" class="w-64 justify-center" @click.prevent="showDemo">
            <icon-base class="mr-2">
              <icon-play />
            </icon-base>
            Multiview demo
          </FormButton>
          <FormButton to="/subscribe" class="w-64 justify-center">
            Upgrade Plan
          </FormButton>
        </template>
      </EmptyState>
    </main>

    <main v-else-if="multiviews.length === 0">
      <EmptyState subtitle="Monitor multiple live sources side by side in a single view.">
        <template #icon>
          <icon-base :width="28" :height="28" fill="none">
            <IconMultiview />
          </icon-base>
        </template>
        <MultiviewTeamPermissionError v-if="teamPermissionError" :email="userEmail" />
        <template v-if="!teamPermissionError" #actions>
          <FormButton type="secondary" class="w-64 justify-center"
            href="https://docs.castr.com/en/articles/10363776-multiview-monitoring-multiple-livestreams-at-once">
            <icon-base class="mr-2">
              <icon-book-open />
            </icon-base>
            How Multiview works
          </FormButton>
          <FormButton type="secondary" class="w-64 justify-center" @click.prevent="showDemo">
            <icon-base class="mr-2">
              <icon-play />
            </icon-base>
            Multiview demo
          </FormButton>
          <Tooltip :active="atMultiviewLimit" wrap-trigger :html="true" :hide="1000">
            <FormButton class="gap-1 w-64 justify-center" :disabled="isMultiviewCreateDisabled" @click="showCreateMultiviewModal">
              <icon-base class="mr-2">
                <icon-plus />
              </icon-base>
              New <span class="hidden md:block">Multiview</span>
            </FormButton>
            <template #content>
              <span class="text-surface-8 text-sm" v-html="multiviewCreateDisabledTitle" />
            </template>
          </Tooltip>
        </template>
      </EmptyState>
    </main>

    <main v-else-if="multiviews.length > 0">
      <ResponsiveToolbar class="mt-4 mb-3">
        <template v-if="showListControls">
        <DropDownMenu ref="sortDropdown" type="secondary" position="left">
          <template #toggle-button>
            <span class="text-surface-8">Sort by:&nbsp;</span>
            <span class="text-white">{{ sortByLabel }}</span>
            <icon-base class="!ml-1 shrink-0 text-surface-8">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
              Newest
              <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
            <DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
              Oldest
              <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
            <DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
              A to Z
              <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
            <DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
              Z to A
              <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
        <ListingSearchInput v-model="searchWidgetInput" />
        </template>
        <ViewModeSwitcher class="shrink-0" />
      </ResponsiveToolbar>

      <section>
        <ul class="grid grid-cols-1 auto-rows-fr gap-2.5 list-none m-0 p-0">
          <li
            v-for="multiview in multiviewFiltered"
            :key="multiview._id"
            class="rounded-lg bg-surface-2"
          >
            <div class="flex items-center gap-3 px-3.5 py-3">
              <router-link
                class="flex min-w-0 flex-1 items-center gap-3 text-inherit hover:no-underline"
                :to="{ name: 'MultiviewContent', params: { id: multiview._id }, query: { sourceTab: 'livestream' } }"
              >
                <icon-base
                  class="shrink-0 text-surface-7"
                  :width="32"
                  :height="32"
                  :width-view-box="16"
                  :height-view-box="16"
                  fill="none"
                >
                  <IconMultiview />
                </icon-base>
                <div class="min-w-0 flex-1">
                  <h2 class="mb-0.5 truncate text-sm font-medium text-white" :title="multiview.name">
                    {{ multiview.name }}
                  </h2>
                  <p class="mb-0 text-tiny text-surface-7">
                    {{ multiviewStreamsNum(multiview) }}/{{ multiview.layout || 0 }} streams
                  </p>
                </div>
              </router-link>

              <!-- Mobile: kebab menu -->
              <DropDownMenu
                is-icon
                position="right"
                type="secondary"
                no-border
                class="shrink-0 md:hidden"
              >
                <template #toggle-button>
                  <icon-base class="text-surface-7">
                    <IconElipsisVertical />
                  </icon-base>
                </template>
                <template #menu-items>
                  <DropDownMenuItem
                    @click="router.push({ name: 'MultiviewContent', params: { id: multiview._id }, query: { sourceTab: 'livestream' } })"
                  >
                    Edit
                  </DropDownMenuItem>
                  <DropDownMenuItem @click="onDeleteMultiviewRequest(multiview._id)">
                    Delete
                  </DropDownMenuItem>
                </template>
              </DropDownMenu>

              <!-- Desktop: Edit + Delete -->
              <div class="hidden md:flex shrink-0 items-center gap-2">
                <FormButton
                  type="secondary"
                  size="sm"
                  :to="{ name: 'MultiviewContent', params: { id: multiview._id }, query: { sourceTab: 'livestream' } }"
                >
                  Edit
                </FormButton>
                <FormButton
                  type="danger"
                  size="sm"
                  is-icon
                  @click.prevent="onDeleteMultiviewRequest(multiview._id)"
                >
                  <icon-base>
                    <icon-bin />
                  </icon-base>
                </FormButton>
              </div>
            </div>
          </li>
          <li v-if="!searchWidgetInput && !teamPermissionError && (!isMultiviewCreateDisabled || atMultiviewLimit)">
            <ListingCreateTile
              label="Multiview"
              view="list"
              :used="multiviews.length"
              :limit="multiviewLimit"
              :limit-reached="atMultiviewLimit"
              @create="showCreateMultiviewModal"
            />
          </li>
        </ul>
      </section>
    </main>
    <demo-video-modal ref="modalDEMOVideo" videoid="cm5nfhg2e05nqm5nfs3y3h7j1" />
    <add-multiview-modal @multiview-added="onNewMV" ref="showMultiviewModal" />

    <confirm-modal modalId="delete-multiview" message="Are you sure you want to delete your Multiview?"
      modal-type="danger" @modal-confirm="onMultiviewDelete" />
  </div>
</template>

<!-- <style scoped>
 .email-tooltip {
	display: inline;
	font-weight: 500 !important;
	font-size: 13px !important;
 }
</style> -->
